/**
 * Default reschedule-timing options for the `create_task` form field.
 *
 * Each option carries:
 *   - key   : stable id stored on the field value & spawned task
 *   - label : human-readable string shown to the provider + scheduler
 *   - days  : numeric offset added to the session DOS to compute the
 *             target appointment date.  `null` means non-numeric
 *             (PRN / Discharge / Other) — the scheduler reads the
 *             label only and uses their judgement.
 *
 * Used by:
 *   - CreateTaskFieldWidget (provider-facing select)
 *   - App.js form builder (template designer picks which options are
 *     available per-field; storing on `field.reschedule_options`)
 *   - TasksPanel + linked-items panel (scheduler display)
 *   - services/create_task_field_processor.py (backend mirror, kept
 *     in sync — see DEFAULT_RESCHEDULE_OPTIONS there)
 */
export const DEFAULT_RESCHEDULE_OPTIONS = [
  { key: 'same_week',  label: 'Same week (urgent)', days: 5 },
  { key: '1_week',     label: '1 week',             days: 7 },
  { key: '2_weeks',    label: '2 weeks',            days: 14 },
  { key: '3_weeks',    label: '3 weeks',            days: 21 },
  { key: '1_month',    label: '1 month',            days: 30 },
  { key: '6_weeks',    label: '6 weeks',            days: 42 },
  { key: '2_months',   label: '2 months',           days: 60 },
  { key: '3_months',   label: '3 months',           days: 90 },
  { key: '6_months',   label: '6 months',           days: 180 },
  { key: 'prn',        label: 'PRN / As needed',    days: null },
  { key: 'discharge',  label: 'Discharge (no reschedule)', days: null },
  { key: 'other',      label: 'Other',              days: null },
];

/** Lookup table keyed by ``option.key`` for O(1) access. */
export const RESCHEDULE_OPTIONS_BY_KEY = Object.fromEntries(
  DEFAULT_RESCHEDULE_OPTIONS.map((o) => [o.key, o]),
);

/**
 * Resolve an option's display label from its key, falling back to a
 * stored ``reschedule_in_label`` if the key is unknown (template uses
 * a custom option set).
 */
export function getRescheduleOptionLabel(key, fallback) {
  if (!key) return fallback || null;
  return RESCHEDULE_OPTIONS_BY_KEY[key]?.label || fallback || key;
}

/**
 * Add ``days`` to a YYYY-MM-DD string and return the result in the
 * same format.  Returns null when either input is missing or the
 * option has no numeric offset (PRN / Discharge / Other).
 */
export function computeTargetDate(sessionDateISO, days) {
  if (!sessionDateISO || !Number.isFinite(parseInt(days, 10))) return null;
  const base = new Date(`${String(sessionDateISO).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(base.getTime())) return null;
  base.setUTCDate(base.getUTCDate() + parseInt(days, 10));
  return base.toISOString().slice(0, 10);
}
